'use client';

import Image from 'next/image';
import React, { useState, useRef, useEffect, TouchEvent } from 'react';
import ImageWithSignedFallback from '../image-rotators/ImageWithSignedFallback';
import { Button, Card, Heading, Overlay } from '@ui';
import { prefersReducedMotion } from '@utils/prefersReducedMotion';
import { IconChevronLeft, IconChevronRight, IconCloseX } from '@icons';

type ImageSliderImage = { key: string; src: string; alt?: string };

interface ImageSliderProps {
  images: Array<ImageSliderImage>;
  headingAs?: React.ElementType;
  headingClassName?: string;
  renderImage?: (img: ImageSliderImage, idx: number) => React.ReactNode;
}

const ImageSlider: React.FC<ImageSliderProps> = ({
  images,
  headingAs = 'h2',
  headingClassName = 'ui-heading',
  renderImage,
}) => {
  const [photo, setPhoto] = useState(0);
  const [overlayIdx, setOverlayIdx] = useState<number | null>(null);
  const len = images.length;
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  const articleRefs = useRef<Map<number, HTMLElement | null>>(new Map());
  const hasAutoScrolledRef = useRef(false);

  const setArticleRef = (idx: number) => (el: HTMLElement | null) => {
    articleRefs.current.set(idx, el);
    return undefined as void;
  };

  useEffect(() => {
    function toggleFocusability(el: HTMLElement | null, active: boolean) {
      if (!el) return;
      if ('inert' in HTMLElement.prototype) {
        try {
          (el as unknown as { inert?: boolean }).inert = !active;
          return;
        } catch {}
      }
      const focusable = el.querySelectorAll<HTMLElement>(
        'a[href], button, input, textarea, select, [tabindex]'
      );
      focusable.forEach((node) => {
        if (!active) {
          if (node.hasAttribute('tabindex')) {
            node.setAttribute('data-prev-tabindex', node.getAttribute('tabindex') || '');
          }
          node.setAttribute('tabindex', '-1');
          if ('disabled' in node) {
            try {
              (node as HTMLButtonElement | HTMLInputElement).disabled = true;
            } catch {}
          }
        } else {
          if (node.hasAttribute('data-prev-tabindex')) {
            const prev = node.getAttribute('data-prev-tabindex');
            if (prev && prev.length > 0) node.setAttribute('tabindex', prev);
            else node.removeAttribute('tabindex');
            node.removeAttribute('data-prev-tabindex');
          } else {
            if (node.getAttribute('tabindex') === '-1') node.removeAttribute('tabindex');
          }
          if ('disabled' in node) {
            try {
              (node as HTMLButtonElement | HTMLInputElement).disabled = false;
            } catch {}
          }
        }
      });
    }
    articleRefs.current.forEach((el, idx) => {
      toggleFocusability(el, idx === photo);
    });
  }, [photo]);

  useEffect(() => {
    const container = thumbnailsRef.current;
    if (!container) return;
    const isFirstRun = !hasAutoScrolledRef.current;
    if (isFirstRun) {
      hasAutoScrolledRef.current = true;
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      if (!hash || hash !== '#image-slider') return;
    }
    const active = container.querySelector<HTMLElement>(`[data-key="${photo}"]`);
    active?.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [photo]);

  const touchStartX = useRef<number | null>(null);

  const goTo = (idx: number) => setPhoto(Math.max(0, Math.min(idx, len - 1)));
  const prev = () => goTo(photo - 1);
  const next = () => goTo(photo + 1);

  const handleThumbnailActivate =
    (idx: number) => (e?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      goTo(idx);

      const container = thumbnailsRef.current;
      if (!container) return;

      const thumbs = Array.from(
        container.querySelectorAll<HTMLElement>('.slider-thumbnail[data-key]')
      );
      if (!thumbs.length) return;

      const target =
        (e?.currentTarget as HTMLElement | undefined) ??
        thumbs.find((el) => Number(el.dataset.key) === idx);
      if (!target) return;

      const canScrollRight =
        container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
      if (!canScrollRight) return;

      const containerRect = container.getBoundingClientRect();

      let lastFullyVisible = -1;
      let firstHiddenToRight: HTMLElement | null = null;

      for (const el of thumbs) {
        const rect = el.getBoundingClientRect();
        const key = Number(el.dataset.key);
        if (Number.isNaN(key)) continue;
        const fullyVisible =
          rect.left >= containerRect.left - 1 && rect.right <= containerRect.right + 1;
        if (fullyVisible) lastFullyVisible = Math.max(lastFullyVisible, key);
        if (!fullyVisible && rect.right > containerRect.right + 1 && !firstHiddenToRight) {
          firstHiddenToRight = el;
        }
      }

      if (idx !== lastFullyVisible) return;
      if (!firstHiddenToRight) return;

      const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';
      firstHiddenToRight.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'start',
      });
    };

  useEffect(() => {
    const handleKeyDown = (e: WindowEventMap['keydown']) => {
      if (e.key === 'ArrowLeft') setPhoto((p) => Math.max(0, Math.min(p - 1, len - 1)));
      if (e.key === 'ArrowRight') setPhoto((p) => Math.max(0, Math.min(p + 1, len - 1)));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [len]);

  const onTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) prev();
    if (delta < -50) next();
    touchStartX.current = null;
  };

  return (
    <Card className='image-slider' ref={sliderRef}>
      <div className='slider-big-images' onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {images.map((img, idx) => (
          <div
            className='slider-big-image'
            key={img.key}
            data-key={idx}
            data-active={photo === idx ? true : undefined}
            aria-hidden={photo !== idx}
          >
            {typeof renderImage === 'function' ? (
              renderImage(img, idx)
            ) : typeof img.src === 'string' &&
              !img.src.startsWith('http') &&
              !img.src.startsWith('/') ? (
              <ImageWithSignedFallback
                src={img.src}
                alt={img.alt || `Image ${idx + 1}`}
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              />
            ) : (
              <Image
                src={img.src}
                alt={img.alt || `Image ${idx + 1}`}
                width={800}
                height={500}
                style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                priority={photo === idx}
                {...(typeof img.src === 'string' && img.src.startsWith('http')
                  ? { unoptimized: true }
                  : {})}
              />
            )}
          </div>
        ))}

        <nav className='slider-nav'>
          <Button
            id='prev'
            icon={<IconChevronLeft />}
            className='carousel-btn prev'
            aria-label='Scroll left'
            onClick={prev}
            as='button'
            type='button'
            disabled={photo === 0}
          />
          <Button
            icon={<IconChevronRight />}
            className='carousel-btn next'
            aria-label='Scroll right'
            onClick={next}
            as='button'
            type='button'
            disabled={photo === len - 1}
          />
        </nav>

        <div className='slider-bottom'>
          <div className='slider-content'>
            <div className='slider-articles'>
              {images.map((img, idx) => (
                <article
                  className='slider-article'
                  key={img.key + '-article'}
                  data-key={idx}
                  data-active={photo === idx ? true : undefined}
                  aria-hidden={photo !== idx}
                  ref={setArticleRef(idx)}
                >
                  <div>
                    <Heading as={headingAs} className={headingClassName}>
                      {img.alt || `Image ${idx + 1}`}
                    </Heading>
                    <p>
                      {' '}
                      Lorem ipsum dolor sit amet, consectetur adipisicing elit. Image {idx + 1}{' '}
                      description.{' '}
                    </p>
                  </div>
                  <div>
                    <Button
                      label='See Image'
                      className='see-image btn-primary'
                      aria-label='See full size image'
                      onClick={() => setOverlayIdx(idx)}
                      as='button'
                      type='button'
                    />
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className='slider-thumbnails' ref={thumbnailsRef}>
            {images.map((img, idx) => (
              <Card
                className='slider-thumbnail'
                key={img.key + '-thumb'}
                data-key={idx}
                data-active={photo === idx ? true : undefined}
                tabIndex={0}
                onClick={handleThumbnailActivate(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleThumbnailActivate(idx)(e);
                  }
                }}
                aria-label={`Go to image ${idx + 1}`}
                role='button'
              >
                {typeof renderImage === 'function' ? (
                  renderImage(img, idx)
                ) : typeof img.src === 'string' &&
                  !img.src.startsWith('http') &&
                  !img.src.startsWith('/') ? (
                  <ImageWithSignedFallback
                    key={img.key + '-thumb'}
                    className='ui-thumbnail'
                    src={img.src}
                    alt={img.alt || `Thumbnail ${idx + 1}`}
                    width={120}
                    height={80}
                    data-key={idx}
                    data-active={photo === idx ? true : undefined}
                    onClick={handleThumbnailActivate(idx)}
                    style={{ cursor: 'pointer' }}
                  />
                ) : (
                  <Image
                    key={img.src + '-thumb'}
                    className='ui-thumbnail'
                    src={img.src}
                    alt={img.alt || `Thumbnail ${idx + 1}`}
                    width={120}
                    height={80}
                    data-key={idx}
                    data-active={photo === idx ? true : undefined}
                    onClick={handleThumbnailActivate(idx)}
                    style={{ cursor: 'pointer' }}
                    {...(typeof img.src === 'string' && img.src.startsWith('http')
                      ? { unoptimized: true }
                      : {})}
                  />
                )}
              </Card>
            ))}
          </div>
        </div>
      </div>

      {overlayIdx !== null && (
        <Overlay open={overlayIdx !== null} onClose={() => setOverlayIdx(null)}>
          <Card className='modal-window image-modal'>
            {typeof renderImage === 'function' ? (
              renderImage(images[overlayIdx], overlayIdx)
            ) : typeof images[overlayIdx].src === 'string' &&
              !images[overlayIdx].src.startsWith('http') &&
              !images[overlayIdx].src.startsWith('/') ? (
              <ImageWithSignedFallback
                src={images[overlayIdx].src}
                alt={images[overlayIdx].alt || `Image ${overlayIdx + 1}`}
                style={{ maxHeight: '80vh', objectFit: 'contain' }}
              />
            ) : (
              <Image
                src={images[overlayIdx].src}
                alt={images[overlayIdx].alt || `Image ${overlayIdx + 1}`}
                width={1200}
                height={800}
                style={{ maxHeight: '80vh', objectFit: 'contain' }}
                priority
                {...(typeof images[overlayIdx].src === 'string' &&
                images[overlayIdx].src.startsWith('http')
                  ? { unoptimized: true }
                  : {})}
              />
            )}
            <Button
              className='btn-close'
              aria-label='Close'
              onClick={() => setOverlayIdx(null)}
              as='button'
              type='button'
              icon={<IconCloseX />}
            />
          </Card>
        </Overlay>
      )}
    </Card>
  );
};

export default ImageSlider;
